/**
 * Truncate a numeric value to 2 decimal places without rounding, as a number.
 * 12.5678 -> 12.56, 12.569 -> 12.56 (not 12.57), 12.5 -> 12.5.
 * Call .toFixed(2) on the result for a display string with trailing zeros.
 * @param {number|string} num
 * @returns {number}
 */
export const trunc2 = (num) => {
  const n = typeof num === 'string' ? parseFloat(num) : num;
  if (!Number.isFinite(n)) return 0;
  // Clean FP noise (e.g. 1 + 0.15 = 1.1499999...) before truncating,
  // so 1.15 doesn't get truncated to 1.14.
  return Math.trunc(Number((n * 100).toFixed(8))) / 100;
};

/**
 * Format a price value with currency symbol and proper formatting
 * @param {number|string} price - The price value to format
 * @param {string} currency - Currency code (default: 'USD')
 * @param {Object} options - Additional formatting options
 * @returns {string} Formatted price string (e.g., "$1,234.56")
 */
export const formatPrice = (price, currency = 'USD', options = {}) => {
  const {
    locale = 'en-US',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showCurrency = true,
  } = options;

  // Handle null, undefined, or invalid values
  if (price === null || price === undefined || price === '') {
    return showCurrency ? '$0.00' : '0.00';
  }

  // Convert to number if string
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

  // Check if valid number
  if (isNaN(numericPrice)) {
    return showCurrency ? '$0.00' : '0.00';
  }

  // Truncate to maximumFractionDigits decimal places (no rounding).
  // toFixed(8) cleans FP noise (e.g. 7.23 * 100 = 722.9999...) before flooring.
  const factor = Math.pow(10, maximumFractionDigits);
  const truncatedPrice = Math.floor(Number((numericPrice * factor).toFixed(8))) / factor;

  // Format using Intl.NumberFormat
  const formatter = new Intl.NumberFormat(locale, {
    style: showCurrency ? 'currency' : 'decimal',
    currency: currency,
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return formatter.format(truncatedPrice);
};

/**
 * Parse a date string as UTC. Database stores UTC without 'Z' suffix,
 * but JS treats ISO strings without 'Z' as local time.
 */
const parseAsUTC = (date) => {
  if (date instanceof Date) return date;
  if (typeof date === 'string' && date.includes('T') && !date.endsWith('Z') && !/[+-]\d{2}(:\d{2})?$/.test(date)) {
    return new Date(date + 'Z');
  }
  return new Date(date);
};

/**
 * Format a date value with various formatting options
 * @param {Date|string|number} date - The date to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  const {
    locale = 'en-US',
    format = 'short', // 'short', 'medium', 'long', 'full', 'relative', 'custom'
    customFormat = {},
  } = options;

  // Handle null or undefined
  if (!date) {
    return '';
  }

  // Convert to Date object (ensure UTC parsing for DB dates)
  const dateObj = parseAsUTC(date);

  // Check if valid date
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  // Handle relative time format (e.g., "2 hours ago", "3 days ago")
  if (format === 'relative') {
    return formatRelativeDate(dateObj);
  }

  // Handle custom format
  if (format === 'custom' && Object.keys(customFormat).length > 0) {
    return new Intl.DateTimeFormat(locale, customFormat).format(dateObj);
  }

  // Predefined formats
  const formatOptions = {
    short: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    },
    medium: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
    full: {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
    datetime: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    time: {
      hour: '2-digit',
      minute: '2-digit',
    },
  };

  const selectedFormat = formatOptions[format] || formatOptions.short;
  return new Intl.DateTimeFormat(locale, selectedFormat).format(dateObj);
};

/**
 * Format a date as relative time (e.g., "2 hours ago", "3 days ago")
 * @param {Date} date - The date to format
 * @returns {string} Relative time string
 */
export const formatRelativeDate = (date) => {
  const now = new Date();
  const dateObj = parseAsUTC(date);
  const diffInSeconds = Math.floor((now - dateObj) / 1000);

  // Handle invalid dates
  if (isNaN(diffInSeconds)) {
    return '';
  }

  // Future dates
  if (diffInSeconds < 0) {
    return 'in the future';
  }

  // Less than a minute
  if (diffInSeconds < 60) {
    return 'just now';
  }

  // Less than an hour
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }

  // Less than a day
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }

  // Less than a week
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }

  // Less than a month
  if (diffInSeconds < 2592000) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }

  // Less than a year
  if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  }

  // Years
  const years = Math.floor(diffInSeconds / 31536000);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
};

/**
 * Format a date range
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (startDate, endDate, options = {}) => {
  const { locale = 'en-US', format = 'medium' } = options;

  const start = formatDate(startDate, { locale, format });
  const end = formatDate(endDate, { locale, format });

  if (!start && !end) return '';
  if (!start) return end;
  if (!end) return start;

  return `${start} - ${end}`;
};

/**
 * Formats a date (and optional time) to ISO 8601 UTC format with microseconds.
 * Used for saving date/time as UTC to the database.
 * @param {string|Date} date - Date to format
 * @param {string} [time] - Optional time string in "HH:MM" format
 * @returns {string} Formatted UTC date string (e.g., "2025-12-05T01:58:17.000000")
 */
export const formatDateToISO = (date, time) => {
  if (!date) return "";

  const dateObj = new Date(date);

  if (isNaN(dateObj.getTime())) return "";

  if (time) {
    const [hours, minutes] = time.split(':');
    dateObj.setHours(+hours, +minutes, 0, 0);
  }

  return dateObj.toISOString(); // ✅ KEEP Z
};

/**
 * Format a date and time into an ISO string using moment-timezone.
 * Interprets the date+time in the given IANA timezone and converts to UTC.
 * @param {string|Date} date - Date to format
 * @param {string} [time] - Optional time string in "HH:MM" format
 * @param {string} [timezone] - IANA timezone (e.g. "America/New_York"). Falls back to UTC.
 * @returns {string} ISO 8601 UTC string
 */
export const formatDateToISOWithTimezone = (date, time, timezone) => {
  const moment = require('moment-timezone');
  if (!date) return "";

  // Handle Date objects - extract year/month/day without timezone conversion
  let dateStr = date;
  if (date instanceof Date) {
    dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  const timeStr = time || "00:00";
  const tz = timezone || "UTC";

  const m = moment.tz(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm", tz);

  if (!m.isValid()) return "";

  return m.utc().toISOString();
};

export const formatUTCToDateTime = (date, timezone, format) => {
    const moment = require('moment-timezone');

    if (!date) return "";

    const tz = timezone || moment.tz.guess();

    const m = moment.utc(date).tz(tz);

    return m.format(format);
}

export const formatDateWithTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = parseAsUTC(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        });
  };
